/**
 * The identity client (D157): the app-side edge of platform.md §5 — Better
 * Auth's browser client behind the one narrow interface the shell renders
 * against.
 *
 * @remarks
 * The interface is deliberately smaller than the library: five verbs and a
 * question, each answering in words the shell can put in front of the
 * player, and never a library error object. That is what lets the account
 * control be tested on a scripted client, and what keeps Better Auth's
 * surface — which is large, and typed against the server's plugin set —
 * inside this one module.
 *
 * Every door lands back on `callbackUrl` (the page that opened it) with
 * the session cookie set for the domain, so `play-api` sees the account on
 * the next request. A door that needs no redirect (a passkey, sign-out)
 * changes who the play cookie means without a navigation; the shell
 * reloads on those, because the open session belonged to someone else.
 *
 * D170 adds the handoff's two verbs — mint, on the home host; accept, on a
 * creator's domain — and the bearer session the second one keeps, which
 * the fetch handed in carries on every request after (`handoff.ts`).
 *
 * @packageDocumentation
 */

import { passkeyClient } from '@better-auth/passkey/client';
import {
	BEARER_SESSION_HEADER,
	devicesAnswerSchema,
	type DeviceSession,
	type DoorsAnswer,
	doorsAnswerSchema,
	handoffMintAnswerSchema,
	IDENTITY_BASE_PATH,
	IDENTITY_DEVICE_REVOKE_PATH,
	IDENTITY_DEVICES_PATH,
	IDENTITY_DEVICES_REVOKE_OTHERS_PATH,
	IDENTITY_DOORS_PATH,
	IDENTITY_HANDOFF_MINT_PATH,
	IDENTITY_HANDOFF_VERIFY_PATH,
	IDENTITY_SIGN_INS_PATH,
	revokeAnswerSchema,
	type SignedInUser,
	signedInUserSchema,
	type SignInEvent,
	signInsAnswerSchema,
	type SocialProvider,
} from '@orpheus/api-contracts';
import { createAuthClient } from 'better-auth/client';
import { magicLinkClient } from 'better-auth/client/plugins';

import { ACCOUNT_NOTICE } from './account-copy.js';
import { DEVICES_NOTICE } from './device-copy.js';
import { type BearerSessionStore } from './handoff.js';

/** What a door answers: it worked, or it did not and here is why in the player's words. */
export type DoorOutcome = { readonly kind: 'done' } | { readonly kind: 'refused'; readonly reason: string };

/** What a revoke answers: done, with how many sessions ended — or refused. */
export type RevokeOutcome = { readonly kind: 'done'; readonly revoked: number } | { readonly kind: 'refused'; readonly reason: string };

/** The identity, as the shell sees it. */
export interface IdentityClient {
	/**
	 * D170, on a foreign hostname: exchange the one-time token this page
	 * arrived with for a bearer session, kept for every request after.
	 */
	acceptHandoff(token: string): Promise<DoorOutcome>;
	/** Signed in: register a passkey for this device. */
	addPasskey(): Promise<DoorOutcome>;
	/** D171, signed in: every session of the account, this one first; nothing when the door refuses. */
	listDevices(): Promise<undefined | readonly DeviceSession[]>;
	/**
	 * D170, on the home host: mint a one-time token for the signed-in
	 * player, to send them back to a creator's domain with. Nothing when
	 * nobody is signed in.
	 */
	mintHandoff(): Promise<string | undefined>;
	/** D173: which doors this identity offers — a mailer, which providers; nothing when it cannot be asked. */
	readDoors(): Promise<DoorsAnswer | undefined>;
	/** The account signed in right now, or nothing. */
	readSession(): Promise<SignedInUser | undefined>;
	/** D171, signed in: the account's sign-ins, newest first; nothing when the door refuses. */
	readSignIns(): Promise<undefined | readonly SignInEvent[]>;
	/** D171, signed in: end one of the account's sessions by its id from the list. */
	revokeDevice(id: string): Promise<RevokeOutcome>;
	/** D171, signed in: end every session of the account but this one. */
	revokeOtherDevices(): Promise<RevokeOutcome>;
	/** Send the magic link; `done` means sent, not signed in. */
	sendMagicLink(email: string): Promise<DoorOutcome>;
	/** Sign in with a passkey already registered for this relying party. */
	signInWithPasskey(): Promise<DoorOutcome>;
	/** Leave for the provider; resolves only when the door refused, since success is a navigation. */
	signInWithSocial(provider: SocialProvider): Promise<DoorOutcome>;
	signOut(): Promise<DoorOutcome>;
}

/** What the client needs from its edge. */
export interface IdentityClientOptions {
	/** The identity Worker's origin — `VITE_IDENTITY_URL`. */
	readonly baseUrl: string;
	/**
	 * D170: where a bearer session is kept. The fetch handed in already
	 * carries it on every request; the client writes it on a handoff and
	 * clears it on sign-out.
	 */
	readonly bearer?: BearerSessionStore;
	/** Where every door lands: this page. */
	readonly callbackUrl: string;
	readonly fetchImplementation?: typeof fetch;
}

/** The library's answer shape: data or an error with a message and the status it came with. */
interface LibraryAnswer {
	readonly error?: { readonly message?: string | undefined; readonly status?: number | undefined } | null;
}

/** The library's limiter refused: too many tries too quickly. */
const TOO_MANY_REQUESTS = 429;

/**
 * A library answer as a door outcome: the error's message when it has one,
 * a plain line when it does not. The limiter's refusal (D173) is said in the
 * register rather than in the library's words, because it is the one refusal
 * every door shares and the one a player meets after trying twice.
 */
const outcomeOf = (answer: LibraryAnswer, fallback: string): DoorOutcome => {
	if (answer.error === null || answer.error === undefined) {
		return { kind: 'done' };
	}

	if (answer.error.status === TOO_MANY_REQUESTS) {
		return { kind: 'refused', reason: ACCOUNT_NOTICE.refused.tooMany };
	}

	return { kind: 'refused', reason: answer.error.message ?? fallback };
};

/**
 * The identity client over Better Auth's browser client.
 *
 * @param options - {@link IdentityClientOptions}
 * @returns The client
 */
export const createIdentityClient = (options: IdentityClientOptions): IdentityClient => {
	const client = createAuthClient({
		basePath: IDENTITY_BASE_PATH,
		baseURL: options.baseUrl,
		fetchOptions: {
			// The Worker is another origin; the session cookie travels only when asked.
			credentials: 'include',
			...(options.fetchImplementation === undefined ? {} : { customFetchImpl: options.fetchImplementation }),
		},
		plugins: [magicLinkClient(), passkeyClient()],
	});
	const { bearer, callbackUrl } = options;
	const fetchImplementation = options.fetchImplementation ?? fetch.bind(globalThis);

	/** One of the two revoke doors (D171): a JSON post with the cookie or bearer along; the count it answers, or the refusal's line. */
	const revokeThrough = async (url: string, body: unknown, refusal: string): Promise<RevokeOutcome> => {
		const answered = await fetchImplementation(url, {
			body: JSON.stringify(body),
			credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			method: 'POST',
		});
		const parsed = answered.ok ? revokeAnswerSchema.safeParse(await answered.json()) : undefined;

		return parsed?.success === true ? { kind: 'done', revoked: parsed.data.revoked } : { kind: 'refused', reason: refusal };
	};

	return {
		acceptHandoff: async (token) => {
			// The contract's door, called plainly: what matters is the one
			// response header the bearer plugin exposes, which the library's
			// typed client does not hand back.
			const exchanged = await fetchImplementation(`${options.baseUrl}${IDENTITY_HANDOFF_VERIFY_PATH}`, {
				body: JSON.stringify({ token }),
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				method: 'POST',
			});
			const session = exchanged.headers.get(BEARER_SESSION_HEADER);

			if (!exchanged.ok || session === null || session.length === 0) {
				return { kind: 'refused', reason: ACCOUNT_NOTICE.refused.acceptHandoff };
			}

			bearer?.write(session);

			return { kind: 'done' };
		},
		addPasskey: async () => outcomeOf(await client.passkey.addPasskey(), ACCOUNT_NOTICE.refused.addPasskey),
		listDevices: async () => {
			const answered = await fetchImplementation(`${options.baseUrl}${IDENTITY_DEVICES_PATH}`, { credentials: 'include' });
			const parsed = answered.ok ? devicesAnswerSchema.safeParse(await answered.json()) : undefined;

			return parsed?.success === true ? parsed.data.devices : undefined;
		},
		mintHandoff: async () => {
			const minted = await fetchImplementation(`${options.baseUrl}${IDENTITY_HANDOFF_MINT_PATH}`, { credentials: 'include' });

			if (!minted.ok) {
				return undefined;
			}

			const parsed = handoffMintAnswerSchema.safeParse(await minted.json());

			return parsed.success ? parsed.data.token : undefined;
		},
		readDoors: async () => {
			const answered = await fetchImplementation(`${options.baseUrl}${IDENTITY_DOORS_PATH}`);
			const parsed = answered.ok ? doorsAnswerSchema.safeParse(await answered.json()) : undefined;

			return parsed?.success === true ? parsed.data : undefined;
		},
		readSession: async () => {
			const { data } = await client.getSession();
			const parsed = signedInUserSchema.safeParse(data?.user);

			return parsed.success ? parsed.data : undefined;
		},
		readSignIns: async () => {
			const answered = await fetchImplementation(`${options.baseUrl}${IDENTITY_SIGN_INS_PATH}`, { credentials: 'include' });
			const parsed = answered.ok ? signInsAnswerSchema.safeParse(await answered.json()) : undefined;

			return parsed?.success === true ? parsed.data.signIns : undefined;
		},
		revokeDevice: async (id) => revokeThrough(`${options.baseUrl}${IDENTITY_DEVICE_REVOKE_PATH}`, { id }, DEVICES_NOTICE.refused.revokeDevice),
		revokeOtherDevices: async () => revokeThrough(`${options.baseUrl}${IDENTITY_DEVICES_REVOKE_OTHERS_PATH}`, {}, DEVICES_NOTICE.refused.revokeOthers),
		sendMagicLink: async (email) => outcomeOf(await client.signIn.magicLink({ callbackURL: callbackUrl, email }), ACCOUNT_NOTICE.refused.sendMagicLink),
		signInWithPasskey: async () => outcomeOf(await client.signIn.passkey(), ACCOUNT_NOTICE.refused.signInWithPasskey),
		signInWithSocial: async (provider) => outcomeOf(await client.signIn.social({ callbackURL: callbackUrl, provider }), ACCOUNT_NOTICE.refused.signInWithSocial),
		signOut: async () => {
			const outcome = outcomeOf(await client.signOut(), ACCOUNT_NOTICE.refused.signOut);

			// The Worker has ended the session; the page forgets the bearer that named it.
			if (outcome.kind === 'done') {
				bearer?.clear();
			}

			return outcome;
		},
	};
};
